/**
 * =========================================================================================
 * SHERGOLD PRIME ERP - PRINT SERVICE
 * =========================================================================================
 *
 * Unified, reusable print engine for all voucher types in the ERP.
 * Replaces hardcoded HTML/CSS print layouts with dynamic pdfme template rendering.
 *
 * USAGE:
 *   import { printWithTemplate } from "../hooks/usePrintService";
 *
 *   // Print receipt using assigned template
 *   await printWithTemplate("SCHEME RECEIPT", data, apiBaseUrl, dbCode, { action: "print" });
 *
 *   // Download PDF
 *   await printWithTemplate("SCHEME RECEIPT", data, apiBaseUrl, dbCode, { action: "download", fileName: "receipt.pdf" });
 *
 *   // Get blob for WhatsApp upload
 *   const blob = await printWithTemplate("SCHEME RECEIPT", data, apiBaseUrl, dbCode, { action: "blob" });
 *
 * ACTIONS:
 *   "print"    - Triggers print dialog directly without opening a new tab
 *   "download" - Triggers browser Save-As dialog with a given filename
 *   "blob"     - Returns raw Blob (for WhatsApp / external upload)
 *   "preview"  - Returns Object URL string (for <iframe> or <embed> preview)
 *
 * ERROR HANDLING:
 *   If no template is assigned, throws:
 *     Error('No print template assigned for "SCHEME RECEIPT"')
 *   Callers should catch this and fall back to the legacy <Print /> modal.
 * =========================================================================================
 */

import { generate } from "@pdfme/generator";
import { getDefaultFont } from "@pdfme/common";
import {
  text,
  image,
  line,
  rectangle,
  table,
  multiVariableText,
  barcodes,
} from "@pdfme/schemas";
import { resolveTemplateInputs } from "../components/Tools/InvoiceDesigner/systemTokens";
import { SAMPLE_TEMPLATES } from "../components/Tools/InvoiceDesigner/sampleTemplates";
import axios from "axios";

// Helper to retrieve auth header if token exists
function getAuthHeaders() {
  try {
    const token = sessionStorage.getItem("authToken") || localStorage.getItem("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (_) {
    return {};
  }
}

// Registered @pdfme plugins (must match InvoiceDesigner & PrintModelAssigner)
const PLUGINS = {
  text,
  image,
  line,
  rectangle,
  table,
  multiVariableText,
  qrcode: barcodes.qrcode,
  ...barcodes,
};

// In-memory template cache to avoid repeated DB fetches per session
const _templateCache = {};

// Persistent BroadcastChannel for instant cross-tab template synchronization
let _syncBroadcastChannel = null;
function getSyncChannel() {
  if (typeof BroadcastChannel !== "undefined" && !_syncBroadcastChannel) {
    try {
      _syncBroadcastChannel = new BroadcastChannel("shergold_print_sync");
    } catch (_) {}
  }
  return _syncBroadcastChannel;
}

/**
 * Clears the in-memory template cache.
 * Call this after a template is reassigned in PrintModelAssigner or modified in InvoiceDesigner.
 * @param {string|null} filter Optional voucherType substring to selectively invalidate (null invalidates all)
 */
export function clearPrintTemplateCache(filter = null) {
  if (!filter) {
    Object.keys(_templateCache).forEach((k) => delete _templateCache[k]);
  } else if (typeof filter === "string") {
    const q = filter.toUpperCase().trim();
    let clearedCount = 0;
    Object.keys(_templateCache).forEach((k) => {
      if (k.toUpperCase().includes(q)) {
        delete _templateCache[k];
        clearedCount++;
      }
    });
    // If no key explicitly matched the filter, purge all keys to ensure stale templates never persist
    if (clearedCount === 0) {
      Object.keys(_templateCache).forEach((k) => delete _templateCache[k]);
    }
  }
}

/**
 * Broadcasts print template changes across all tabs, windows, and active vouchers in real-time.
 * Uses BroadcastChannel, window CustomEvent, and localStorage cross-tab triggers.
 */
export function notifyPrintTemplateUpdated(payload = {}) {
  // Always wipe all cached templates so every voucher re-resolves the fresh design immediately
  clearPrintTemplateCache();

  // 1. Cross-tab sync via BroadcastChannel (instant in modern browsers)
  try {
    const channel = getSyncChannel();
    if (channel) {
      channel.postMessage({ type: "printTemplateUpdated", ...payload, timestamp: Date.now() });
    }
  } catch (_) {}

  // 2. In-tab custom event
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent("printTemplateUpdated", { detail: payload }));
    } catch (_) {}
  }

  // 3. Cross-tab storage event trigger for all other tabs
  try {
    localStorage.setItem("shergold_print_template_version", Date.now().toString());
  } catch (_) {}
}

/**
 * Attaches a socket.io instance to listen for real-time print template changes from backend.
 */
export function registerPrintServiceSocket(socket) {
  if (!socket) return;
  socket.on("printTemplateUpdated", (data) => {
    clearPrintTemplateCache();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("printTemplateUpdated", { detail: data }));
    }
  });
}

// Setup autonomous cross-tab and cross-window sync listeners on module load
if (typeof window !== "undefined") {
  // Listen to BroadcastChannel
  try {
    const syncChannel = getSyncChannel();
    if (syncChannel) {
      syncChannel.onmessage = (event) => {
        clearPrintTemplateCache();
        window.dispatchEvent(new CustomEvent("printTemplateUpdated", { detail: event.data }));
      };
    }
  } catch (_) {}

  // Listen to storage event (cross-tab fallback)
  window.addEventListener("storage", (e) => {
    if (
      e.key === "shergold_print_template_version" ||
      (e.key && (e.key.startsWith("shergold_print_templates") || e.key.startsWith("shergold_print_assignments")))
    ) {
      clearPrintTemplateCache();
      window.dispatchEvent(new CustomEvent("printTemplateUpdated", { detail: { fromStorage: true } }));
    }
  });

  // Listen to in-tab CustomEvent
  window.addEventListener("printTemplateUpdated", (e) => {
    clearPrintTemplateCache();
  });
}

/**
 * Fetches and caches the active template for a given voucher type & location.
 * Resolves through:
 * 1. Single active template API endpoint (/main/printtemplates/active/...)
 * 2. Assignments list API endpoint (/main/printtemplates/assignments/...)
 * 3. LocalStorage active assignments (shergold_print_assignments_${dbCode})
 * 4. LocalStorage registered templates matching voucher type
 * 5. Factory SAMPLE_TEMPLATES presets
 */
async function fetchActiveTemplate(voucherType, apiBaseUrl, dbCode, location = 0, noCache = false) {
  const normalizedDb = dbCode || "DEFAULT";
  const normalizedType = (voucherType || "").trim().toUpperCase();
  const cacheKey = `${normalizedDb}_${normalizedType}_${location}`;

  if (!noCache && _templateCache[cacheKey]) {
    return _templateCache[cacheKey];
  }

  const headers = getAuthHeaders();

  // Generate candidate voucher aliases for flexible routing (e.g. "SCHEME RECEIPT PDF" -> "SCHEME RECEIPT")
  const candidates = [normalizedType];
  if (normalizedType.endsWith(" PDF")) {
    candidates.push(normalizedType.replace(/ PDF$/i, "").trim());
  }
  if (normalizedType === "CASH RECEIPT") candidates.push("RECEIPT");
  if (normalizedType === "SCHEME CASH RECEIPT") candidates.push("SCHEME RECEIPT", "RECEIPT");
  if (normalizedType === "CASH PAYMENT") candidates.push("PAYMENT");
  if (normalizedType === "SALE INVOICE") candidates.push("SALE", "INVOICE");

  // ── Step 1: Try backend active template endpoint for each candidate ──
  for (const candidate of candidates) {
    try {
      const activeRes = await axios.get(
        `${apiBaseUrl}/main/printtemplates/active/${normalizedDb}/${encodeURIComponent(candidate)}?location=${location}`,
        { headers }
      );
      if (activeRes.data && (activeRes.data.TemplateJson || activeRes.data.schemas)) {
        const rawJson = activeRes.data.TemplateJson ?? activeRes.data;
        const template = typeof rawJson === "string" ? JSON.parse(rawJson) : rawJson;
        if (template && template.schemas) {
          _templateCache[cacheKey] = template;
          return template;
        }
      }
    } catch (_) {
      // Endpoint may not be installed or no active assignment; proceed to next steps
    }
  }

  // ── Step 2: Fetch active assignments list from Backend ──
  let apiAssignment = null;
  try {
    const assignRes = await axios.get(
      `${apiBaseUrl}/main/printtemplates/assignments/${normalizedDb}?location=${location}`,
      { headers }
    );
    const assignments = Array.isArray(assignRes.data) ? assignRes.data : [];
    // Sort so exact location match takes priority over global default (0)
    assignments.sort((a, b) => {
      const aExact = Number(a.Location) === Number(location) ? 1 : 0;
      const bExact = Number(b.Location) === Number(location) ? 1 : 0;
      return bExact - aExact;
    });

    for (const candidate of candidates) {
      const found = assignments.find(
        (a) => (a.VoucherType || "").trim().toUpperCase() === candidate
      );
      if (found && found.TemplateId) {
        apiAssignment = found;
        break;
      }
    }
  } catch (_) {}

  // Fetch template by ID if an assignment was found from backend
  if (apiAssignment && apiAssignment.TemplateId) {
    const idUrls = [
      `${apiBaseUrl}/main/printtemplates/byId/${normalizedDb}/${apiAssignment.TemplateId}`,
      `${apiBaseUrl}/main/printtemplates/${normalizedDb}/${apiAssignment.TemplateId}`,
    ];
    for (const url of idUrls) {
      try {
        const tplRes = await axios.get(url, { headers });
        const rawJson = tplRes.data?.TemplateJson ?? tplRes.data;
        const template = typeof rawJson === "string" ? JSON.parse(rawJson) : rawJson;
        if (template && template.schemas) {
          _templateCache[cacheKey] = template;
          return template;
        }
      } catch (_) {}
    }
  }

  // ── Step 3: Check LocalStorage Active Assignments (Synced from PrintModelAssigner) ──
  try {
    const localAssignKeys = [
      `shergold_print_assignments_${normalizedDb}`,
      `shergold_print_assignments_DEFAULT`,
      `shergold_print_assignments`
    ];
    let localAssignments = [];
    for (const k of localAssignKeys) {
      const raw = localStorage.getItem(k);
      if (raw) {
        try {
          localAssignments = JSON.parse(raw);
          if (Array.isArray(localAssignments) && localAssignments.length > 0) break;
        } catch (_) {}
      }
    }

    if (Array.isArray(localAssignments) && localAssignments.length > 0) {
      // Sort: exact branch location first, then global 0
      localAssignments.sort((a, b) => {
        const aExact = Number(a.Location) === Number(location) ? 1 : 0;
        const bExact = Number(b.Location) === Number(location) ? 1 : 0;
        return bExact - aExact;
      });

      let matchedLocalAssign = null;
      for (const candidate of candidates) {
        matchedLocalAssign = localAssignments.find(
          (a) => (a.VoucherType || "").trim().toUpperCase() === candidate
        );
        if (matchedLocalAssign) break;
      }

      if (matchedLocalAssign) {
        // Resolve the assigned template from localStorage templates
        const localTplKeys = [
          `shergold_print_templates_${normalizedDb}`,
          `shergold_print_templates_DEFAULT`,
          `shergold_print_templates`
        ];
        for (const tk of localTplKeys) {
          const rawTpls = localStorage.getItem(tk);
          if (rawTpls) {
            try {
              const tpls = JSON.parse(rawTpls);
              const foundTpl = tpls.find(
                (t) =>
                  (matchedLocalAssign.TemplateId && (t.id === matchedLocalAssign.TemplateId || Number(t.id) === Number(matchedLocalAssign.TemplateId))) ||
                  (matchedLocalAssign.TemplateName && (t.templateName || "").trim().toLowerCase() === (matchedLocalAssign.TemplateName || "").trim().toLowerCase())
              );
              if (foundTpl) {
                const rawJson = foundTpl.templateJson || foundTpl.TemplateJson || foundTpl.template;
                const template = typeof rawJson === "string" ? JSON.parse(rawJson) : rawJson;
                if (template && template.schemas) {
                  _templateCache[cacheKey] = template;
                  return template;
                }
              }
            } catch (_) {}
          }
        }
      }
    }
  } catch (_) {}

  // ── Step 4: Fallback: check localStorage for templates matching voucher type directly ──
  try {
    const localTplKeys = [
      `shergold_print_templates_${normalizedDb}`,
      `shergold_print_templates_DEFAULT`,
      `shergold_print_templates`
    ];
    for (const tk of localTplKeys) {
      const raw = localStorage.getItem(tk);
      if (raw) {
        const localTemplates = JSON.parse(raw || "[]");
        for (const candidate of candidates) {
          const matched = localTemplates.find(
            (t) => (t.voucherType || t.VoucherType || "").trim().toUpperCase() === candidate
          );
          if (matched) {
            const rawJson = matched.templateJson || matched.TemplateJson || matched.template;
            const template = typeof rawJson === "string" ? JSON.parse(rawJson) : rawJson;
            if (template && template.schemas) {
              _templateCache[cacheKey] = template;
              return template;
            }
          }
        }
      }
    }
  } catch (_) {}

  // ── Step 5: Fallback: check factory SAMPLE_TEMPLATES ──
  for (const candidate of candidates) {
    const presetEntry = Object.values(SAMPLE_TEMPLATES).find((p) => {
      const pType = (p.voucherType || "").trim().toUpperCase();
      return (
        pType === candidate ||
        (candidate.includes("SCHEME") && p.id === "thermal58mmReceipt") ||
        (candidate.includes("RECEIPT") && p.id === "thermal58mmReceipt")
      );
    });

    if (presetEntry && presetEntry.template && presetEntry.template.schemas) {
      return presetEntry.template;
    }
  }

  throw new Error(`No print template assigned for "${voucherType}"`);
}

/**
 * printWithTemplate
 * -----------------
 * Main entry point. Fetches the active assigned template, resolves token values,
 * generates a PDF via @pdfme/generator, and delivers it via the specified action.
 *
 * @param {string} voucherType  - e.g. "SCHEME RECEIPT"
 * @param {object} data         - Flat token data map: { VoucherNo, Date, CustomerName, ... }
 * @param {string} apiBaseUrl   - Backend API base URL
 * @param {string} dbCode       - Active tenant database code
 * @param {object} [options]
 *   @param {string}  [options.action="print"]     - "print" | "download" | "blob" | "preview"
 *   @param {number}  [options.location=0]         - Showroom location ID
 *   @param {string}  [options.fileName="receipt.pdf"] - Filename for download action
 *   @param {boolean} [options.noCache=false]      - Bypass in-memory template cache
 *
 * @returns {Promise<Blob|string>}
 * @throws {Error} If no template is assigned or template is malformed
 */
/**
 * Automatically centers template schema elements vertically if the content occupies
 * only a portion of a sheet page (e.g. A5 half-page receipt on an A4 sheet), leaving
 * equal margin on top and bottom instead of sticking to the top edge.
 */
function centerTemplateContentVertically(template) {
  if (!template || !Array.isArray(template.schemas) || !template.schemas.length) {
    return template;
  }

  // Determine page dimensions (fallback to standard A4 210 × 297 mm if not numeric)
  let pageHeight = Number(template.basePdf?.height);
  let pageWidth = Number(template.basePdf?.width);
  if (!pageHeight || isNaN(pageHeight)) pageHeight = 297;
  if (!pageWidth || isNaN(pageWidth)) pageWidth = 210;

  // Do not vertically center thermal rolls (width <= 90mm) as continuous paper feeds from top
  if (pageWidth <= 90) {
    return template;
  }

  // Deep clone to prevent mutating original or cached template object
  const cloned = JSON.parse(JSON.stringify(template));

  cloned.schemas = cloned.schemas.map((pageElements) => {
    if (!Array.isArray(pageElements) || pageElements.length === 0) return pageElements;

    let minY = Infinity;
    let maxY = -Infinity;

    pageElements.forEach((el) => {
      const y = Number(el?.position?.y ?? el?.position?.Y);
      const h = Number(el?.height ?? el?.Height) || 0;
      if (!isNaN(y)) {
        if (y < minY) minY = y;
        if (y + h > maxY) maxY = y + h;
      }
    });

    if (minY === Infinity || maxY === -Infinity) return pageElements;

    const contentHeight = maxY - minY;
    const remainingSpace = pageHeight - contentHeight;

    // Center if content takes less than 85% of page height and there is at least 20mm unused vertical space
    if (remainingSpace > 20 && contentHeight < pageHeight * 0.85) {
      const targetMinY = remainingSpace / 2;
      const offsetY = targetMinY - minY;

      if (Math.abs(offsetY) > 2) {
        return pageElements.map((el) => {
          const currentY = Number(el?.position?.y ?? el?.position?.Y);
          if (!isNaN(currentY) && el?.position) {
            return {
              ...el,
              position: {
                ...el.position,
                y: Math.round((currentY + offsetY) * 100) / 100,
              },
            };
          }
          return el;
        });
      }
    }

    return pageElements;
  });

  return cloned;
}

export async function printWithTemplate(
  voucherType,
  data,
  apiBaseUrl,
  dbCode,
  options = {}
) {
  const {
    action = "print",
    location = 0,
    fileName = "receipt.pdf",
    noCache = true, // By default always resolve fresh assignments & saved designs so user updates apply immediately
  } = options;

  if (noCache) {
    const cacheKey = `${dbCode || "DEFAULT"}_${(voucherType || "").trim().toUpperCase()}_${location}`;
    delete _templateCache[cacheKey];
  }

  // 1. Fetch active template
  const rawTemplate = await fetchActiveTemplate(voucherType, apiBaseUrl, dbCode, location, noCache);

  // Auto-center vertically for partial sheet layouts (e.g. A5 receipt on A4 sheet)
  const template = options.centerVertically !== false
    ? centerTemplateContentVertically(rawTemplate)
    : rawTemplate;

  // 2. Resolve tokens -> pdfme inputs array
  const inputs = resolveTemplateInputs(template, data);

  // 3. Generate PDF bytes
  const pdfBytes = await generate({
    template,
    inputs,
    plugins: PLUGINS,
    options: { font: getDefaultFont() },
  });

  const blob = new Blob([pdfBytes], { type: "application/pdf" });

  // 4. Dispatch based on action
  if (action === "blob") {
    return blob;
  }

  if (action === "preview") {
    return URL.createObjectURL(blob);
  }

  if (action === "download") {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 15000);
    return url;
  }

  // Default: "print" - print in place without opening a new browser tab
  const url = URL.createObjectURL(blob);

  // Remove any previous print iframe to avoid stale memory or duplicate listeners
  const existingIframe = document.getElementById("__printServiceIframe__");
  if (existingIframe) {
    existingIframe.remove();
  }

  const iframe = document.createElement("iframe");
  iframe.id = "__printServiceIframe__";
  iframe.style.position = "fixed";
  iframe.style.top = "-9999px";
  iframe.style.left = "-9999px";
  iframe.style.width = "1px";
  iframe.style.height = "1px";
  iframe.style.border = "none";
  iframe.style.opacity = "0.01";
  document.body.appendChild(iframe);

  const cleanup = () => {
    setTimeout(() => {
      try {
        URL.revokeObjectURL(url);
        iframe.remove();
      } catch (_) {}
    }, 60000);
  };

  const triggerPrint = () => {
    try {
      if (iframe.contentWindow) {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      }
    } catch (err) {
      console.warn("Direct iframe print failed, falling back to window:", err);
      const printWindow = window.open(url, "_blank");
      if (printWindow) {
        printWindow.focus();
        printWindow.print();
      }
    }
    cleanup();
  };

  iframe.onload = () => {
    // Small timeout ensures the PDF engine within the iframe finishes mounting
    setTimeout(triggerPrint, 250);
  };

  iframe.src = url;

  return url;
}
